<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import Modal from 'dashboard/components/Modal.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import { formatFlagDisplayText } from 'shared/helpers/flagEmoji';

export default {
  name: 'LabelsTestPage',
  components: {
    Spinner,
    Modal,
    AddLabelModal,
  },
  data() {
    return {
      loading: {
        labels: false,
        contacts: false,
      },
      labels: [],
      allContacts: [],
      filteredContacts: [],
      selectedContacts: [],
      selectedLabels: [],
      searchQuery: '',
      currentPage: 1,
      pageSize: 25,
      showAddLabelModal: false,
    };
  },
  computed: {
    ...mapGetters({ currentAccountId: 'getCurrentAccountId' }),

    filteredContactsCount() {
      return this.filteredContacts.length;
    },

    totalPages() {
      if (this.pageSize >= this.filteredContacts.length) return 1;
      return Math.ceil(this.filteredContacts.length / this.pageSize);
    },

    displayedContacts() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredContacts.slice(start, end);
    },

    allSelected() {
      return (
        this.displayedContacts.length > 0 &&
        this.displayedContacts.every(contact => this.isContactSelected(contact))
      );
    },
  },
  mounted() {
    console.log(
      'LabelsTestPage mounted, showAddLabelModal:',
      this.showAddLabelModal
    );
    this.fetchLabels();
    this.fetchAllContacts();
  },
  methods: {
    formatContactName(name) {
      return formatFlagDisplayText(name || '');
    },
    getCookie(name) {
      const match = document.cookie.match(
        new RegExp('(^| )' + name + '=([^;]+)')
      );
      return match ? decodeURIComponent(match[2]) : null;
    },

    getAuthHeaders() {
      const cookie = this.getCookie('cw_d_session_info');
      if (!cookie) return {};
      try {
        const sessionInfo = JSON.parse(cookie);
        return {
          'access-token': sessionInfo['access-token'],
          client: sessionInfo.client,
          uid: sessionInfo.uid,
        };
      } catch (e) {
        console.error('Error reading cookie:', e);
        return {};
      }
    },

    showAlert(message, variant = 'success') {
      console.log(`[${variant.toUpperCase()}] ${message}`);
    },

    async fetchLabels() {
      this.loading.labels = true;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get(`/api/v1/bulk_messages/labels`, {
          headers,
          withCredentials: true,
        });

        this.labels = response.data.labels || [];
        this.showAlert(
          `Successfully fetched ${this.labels.length} labels`,
          'success'
        );
      } catch (error) {
        console.error('Error fetching labels:', error);
        this.showAlert(`Error fetching labels: ${error.message}`, 'error');
      } finally {
        this.loading.labels = false;
      }
    },

    async fetchAllContacts() {
      this.loading.contacts = true;
      this.allContacts = [];
      try {
        const headers = this.getAuthHeaders();

        // First, get total count
        const countResponse = await axios.get(
          `/api/v1/bulk_messages/contacts`,
          {
            headers,
            withCredentials: true,
            params: { page: 1, per_page: 1 },
          }
        );

        const totalCount = countResponse.data.meta.total_count;
        this.showAlert(`Found ${totalCount} total contacts`, 'info');

        // Fetch all contacts in batches
        const batchSize = 500;
        const totalPages = Math.ceil(totalCount / batchSize);

        for (let page = 1; page <= totalPages; page++) {
          const response = await axios.get(`/api/v1/bulk_messages/contacts`, {
            headers,
            withCredentials: true,
            params: {
              page,
              per_page: batchSize,
            },
          });

          this.allContacts = [...this.allContacts, ...response.data.data];
          this.showAlert(
            `Loaded ${this.allContacts.length} of ${totalCount} contacts...`,
            'info'
          );
        }

        // Now fetch contacts with cached_label_list for each label
        for (const label of this.labels) {
          const labelResponse = await axios.get(
            `/api/v1/bulk_messages/contacts`,
            {
              headers,
              withCredentials: true,
              params: {
                page: 1,
                per_page: 1000,
                label: label.title,
              },
            }
          );

          // Update contacts with label information
          labelResponse.data.data.forEach(labeledContact => {
            const contactIndex = this.allContacts.findIndex(
              c => c.id === labeledContact.id
            );
            if (contactIndex !== -1) {
              this.allContacts[contactIndex].cached_label_list =
                labeledContact.cached_label_list;
            }
          });
        }

        this.filterContacts();
        this.showAlert(
          `Successfully loaded ${this.allContacts.length} contacts`,
          'success'
        );
      } catch (error) {
        console.error('Error fetching contacts:', error);
        this.showAlert(`Error fetching contacts: ${error.message}`, 'error');
      } finally {
        this.loading.contacts = false;
      }
    },

    filterContacts() {
      let filtered = [...this.allContacts];

      // Filter by selected labels
      if (this.selectedLabels.length > 0) {
        filtered = filtered.filter(contact => {
          const contactLabels = this.parseLabels(contact.cached_label_list);
          return this.selectedLabels.some(selectedLabel =>
            contactLabels.includes(selectedLabel.title)
          );
        });
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          contact =>
            (contact.name && contact.name.toLowerCase().includes(query)) ||
            (contact.email && contact.email.toLowerCase().includes(query)) ||
            (contact.phone_number && contact.phone_number.includes(query))
        );
      }

      this.filteredContacts = filtered;
      this.currentPage = 1; // Reset to first page when filtering
    },

    toggleLabelFilter(label) {
      const index = this.selectedLabels.findIndex(l => l.id === label.id);
      if (index > -1) {
        this.selectedLabels.splice(index, 1);
      } else {
        this.selectedLabels.push(label);
      }
      this.filterContacts();
    },

    clearLabelFilter() {
      this.selectedLabels = [];
      this.filterContacts();
    },

    isLabelSelected(label) {
      return this.selectedLabels.some(l => l.id === label.id);
    },

    getContactCountForLabel(labelTitle) {
      return this.allContacts.filter(contact => {
        const labels = this.parseLabels(contact.cached_label_list);
        return labels.includes(labelTitle);
      }).length;
    },

    toggleContactSelection(contact) {
      const index = this.selectedContacts.findIndex(c => c.id === contact.id);
      if (index > -1) {
        this.selectedContacts.splice(index, 1);
      } else {
        this.selectedContacts.push(contact);
      }
    },

    isContactSelected(contact) {
      return this.selectedContacts.some(c => c.id === contact.id);
    },

    toggleSelectAll() {
      if (this.allSelected) {
        // Deselect all displayed contacts
        this.displayedContacts.forEach(contact => {
          const index = this.selectedContacts.findIndex(
            c => c.id === contact.id
          );
          if (index > -1) {
            this.selectedContacts.splice(index, 1);
          }
        });
      } else {
        // Select all displayed contacts
        this.displayedContacts.forEach(contact => {
          if (!this.isContactSelected(contact)) {
            this.selectedContacts.push(contact);
          }
        });
      }
    },

    clearSelection() {
      this.selectedContacts = [];
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    parseLabels(cached_label_list) {
      if (!cached_label_list) return [];
      return cached_label_list
        .split(',')
        .map(label => label.trim())
        .filter(label => label.length > 0);
    },

    getLabelColor(labelTitle) {
      const label = this.labels.find(l => l.title === labelTitle);
      return label ? label.color : '#999999';
    },

    showAddLabelPopup() {
      console.log('Add Label button clicked!');
      this.showAddLabelModal = true;
    },

    hideAddLabelPopup() {
      this.showAddLabelModal = false;
    },
  },
};
</script>

<template>
  <div class="labels-test-page">
    <div class="header-section">
      <h1>Contacts & Labels Management</h1>
      <div class="header-actions">
        <button
          class="button button--primary"
          style="
            background-color: #3b82f6;
            color: white;
            padding: 8px 16px;
            border-radius: 4px;
          "
          @click="showAddLabelPopup"
        >
          + Add Label
        </button>
        <button
          :disabled="loading.labels"
          class="button button--secondary"
          @click="fetchLabels"
        >
          <Spinner v-if="loading.labels" size="small" />
          <span v-else>Refresh Labels</span>
        </button>
        <button
          :disabled="loading.contacts"
          class="button button--secondary"
          @click="fetchAllContacts"
        >
          <Spinner v-if="loading.contacts" size="small" />
          <span v-else>Load All Contacts</span>
        </button>
      </div>
    </div>

    <!-- Quick Stats -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-value">{{ labels.length }}</div>
        <div class="stat-label">Total Labels</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ allContacts.length }}</div>
        <div class="stat-label">Total Contacts</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ filteredContactsCount }}</div>
        <div class="stat-label">Filtered Contacts</div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Sidebar with Labels -->
      <div class="sidebar">
        <div class="sidebar-header">
          <h2>Labels</h2>
          <span class="count-badge">{{ labels.length }}</span>
        </div>

        <div class="label-filters">
          <button
            class="label-filter-item"
            :class="{ active: selectedLabels.length === 0 }"
            @click="clearLabelFilter"
          >
            <span class="label-icon">🏷️</span>
            <span>All Contacts</span>
            <span class="label-count">{{ allContacts.length }}</span>
          </button>

          <div class="label-filter-divider" />

          <button
            v-for="label in labels"
            :key="label.id"
            class="label-filter-item"
            :class="{ active: isLabelSelected(label) }"
            @click="toggleLabelFilter(label)"
          >
            <span
              class="label-color-dot"
              :style="{ backgroundColor: label.color }"
            />
            <span class="label-name">{{ label.title }}</span>
            <span class="label-count">{{
              getContactCountForLabel(label.title)
            }}</span>
          </button>
        </div>
      </div>

      <!-- Contacts List -->
      <div class="content-area">
        <div class="content-header">
          <h2>
            Contacts
            {{
              selectedLabels.length > 0
                ? `(Filtered by ${selectedLabels.length} label${selectedLabels.length > 1 ? 's' : ''})`
                : ''
            }}
          </h2>

          <div class="content-controls">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search contacts..."
              class="search-input"
              @input="filterContacts"
            />

            <select
              v-model="pageSize"
              class="page-size-select"
              @change="filterContacts"
            >
              <option :value="10">10 per page</option>
              <option :value="25">25 per page</option>
              <option :value="50">50 per page</option>
              <option :value="100">100 per page</option>
              <option :value="allContacts.length">All</option>
            </select>
          </div>
        </div>

        <div v-if="loading.contacts" class="content-loading">
          <Spinner />
          <p>Loading contacts...</p>
        </div>

        <div v-else-if="displayedContacts.length === 0" class="no-data">
          <p v-if="allContacts.length === 0">
            No contacts found. Click "Load All Contacts" to fetch contacts.
          </p>
          <p v-else>No contacts match your filter criteria.</p>
        </div>

        <div v-else class="contacts-grid">
          <div class="contacts-table-wrapper">
            <table class="contacts-table">
              <thead>
                <tr>
                  <th width="5%">
                    <input
                      type="checkbox"
                      :checked="allSelected"
                      @change="toggleSelectAll"
                    />
                  </th>
                  <th width="5%">ID</th>
                  <th width="20%">Name</th>
                  <th width="20%">Email</th>
                  <th width="15%">Phone</th>
                  <th width="25%">Labels</th>
                  <th width="10%">Created</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="contact in displayedContacts"
                  :key="contact.id"
                  :class="{ selected: isContactSelected(contact) }"
                >
                  <td>
                    <input
                      type="checkbox"
                      :checked="isContactSelected(contact)"
                      @change="toggleContactSelection(contact)"
                    />
                  </td>
                  <td>{{ contact.id }}</td>
                  <td>
                    <div
                      class="contact-name"
                      v-dompurify-html="
                        formatContactName(contact.name || 'Unnamed Contact')
                      "
                    ></div>
                  </td>
                  <td>
                    <div class="contact-email">
                      {{ contact.email || '-' }}
                    </div>
                  </td>
                  <td>
                    <div class="contact-phone">
                      {{ contact.phone_number || '-' }}
                    </div>
                  </td>
                  <td>
                    <div class="contact-labels">
                      <span
                        v-for="(label, index) in parseLabels(
                          contact.cached_label_list
                        )"
                        :key="index"
                        class="label-badge mini"
                        :style="{ backgroundColor: getLabelColor(label) }"
                        :title="label"
                      >
                        {{ label }}
                      </span>
                      <span
v-if="!contact.cached_label_list" class="no-labels"
                        >No labels</span
                      >
                    </div>
                  </td>
                  <td>
                    <div class="contact-date">
                      {{ formatDate(contact.created_at) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="pagination">
            <button
              :disabled="currentPage === 1"
              class="pagination-btn"
              @click="currentPage = 1"
            >
              First
            </button>
            <button
              :disabled="currentPage === 1"
              class="pagination-btn"
              @click="currentPage--"
            >
              Previous
            </button>
            <span class="pagination-info">
              Page {{ currentPage }} of {{ totalPages }}
            </span>
            <button
              :disabled="currentPage === totalPages"
              class="pagination-btn"
              @click="currentPage++"
            >
              Next
            </button>
            <button
              :disabled="currentPage === totalPages"
              class="pagination-btn"
              @click="currentPage = totalPages"
            >
              Last
            </button>
          </div>
        </div>

        <!-- Selected Contacts Actions -->
        <div v-if="selectedContacts.length > 0" class="selected-actions">
          <div class="selected-info">
            {{ selectedContacts.length }} contact{{
              selectedContacts.length > 1 ? 's' : ''
            }}
            selected
          </div>
          <button class="button button--secondary" @click="clearSelection">
            Clear Selection
          </button>
        </div>
      </div>
    </div>

    <!-- Add Label Modal -->
    <Modal :show="showAddLabelModal" :on-close="hideAddLabelPopup">
      <AddLabelModal @close="hideAddLabelPopup" />
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.labels-test-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--s-50);

  .header-section {
    background: var(--white);
    border-bottom: 1px solid var(--s-200);
    padding: var(--space-normal) var(--space-large);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h1 {
      font-size: var(--font-size-big);
      margin: 0;
      color: var(--s-900);
    }

    .header-actions {
      display: flex;
      gap: var(--space-small);
    }
  }

  .stats-row {
    padding: var(--space-normal) var(--space-large);
    display: flex;
    gap: var(--space-normal);

    .stat-card {
      background: var(--white);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-medium);
      padding: var(--space-normal);
      flex: 1;
      text-align: center;

      .stat-value {
        font-size: var(--font-size-big);
        font-weight: var(--font-weight-bold);
        color: var(--n-brand);
      }

      .stat-label {
        font-size: var(--font-size-small);
        color: var(--s-600);
        margin-top: var(--space-micro);
      }
    }
  }

  .main-content {
    flex: 1;
    display: flex;
    overflow: hidden;
    padding: 0 var(--space-large) var(--space-large);
    gap: var(--space-normal);
  }

  .sidebar {
    width: 280px;
    background: var(--white);
    border: 1px solid var(--s-200);
    border-radius: var(--border-radius-medium);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .sidebar-header {
      padding: var(--space-normal);
      border-bottom: 1px solid var(--s-200);
      display: flex;
      justify-content: space-between;
      align-items: center;

      h2 {
        font-size: var(--font-size-medium);
        margin: 0;
      }

      .count-badge {
        background: var(--s-100);
        color: var(--s-700);
        padding: 2px 8px;
        border-radius: 12px;
        font-size: var(--font-size-mini);
        font-weight: var(--font-weight-medium);
      }
    }

    .label-filters {
      flex: 1;
      overflow-y: auto;

      .label-filter-item {
        width: 100%;
        padding: var(--space-small) var(--space-normal);
        border: none;
        background: transparent;
        display: flex;
        align-items: center;
        gap: var(--space-small);
        cursor: pointer;
        transition: all 0.2s;
        text-align: left;

        &:hover {
          background: var(--s-50);
        }

        &.active {
          background: var(--n-brand-light, var(--s-100));
          color: var(--n-brand);

          .label-count {
            background: var(--n-brand);
            color: white;
          }
        }

        .label-icon {
          font-size: var(--font-size-default);
        }

        .label-color-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .label-name {
          flex: 1;
          font-size: var(--font-size-small);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .label-count {
          background: var(--s-100);
          color: var(--s-700);
          padding: 2px 6px;
          border-radius: 10px;
          font-size: var(--font-size-micro);
          font-weight: var(--font-weight-medium);
        }
      }

      .label-filter-divider {
        height: 1px;
        background: var(--s-200);
        margin: var(--space-small) 0;
      }
    }
  }

  .content-area {
    flex: 1;
    background: var(--white);
    border: 1px solid var(--s-200);
    border-radius: var(--border-radius-medium);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .content-header {
      padding: var(--space-normal);
      border-bottom: 1px solid var(--s-200);
      display: flex;
      justify-content: space-between;
      align-items: center;

      h2 {
        font-size: var(--font-size-medium);
        margin: 0;
      }

      .content-controls {
        display: flex;
        gap: var(--space-small);

        .search-input {
          padding: var(--space-small) var(--space-normal);
          border: 1px solid var(--s-300);
          border-radius: var(--border-radius-small);
          font-size: var(--font-size-small);
          width: 250px;

          &:focus {
            outline: none;
            border-color: var(--n-brand);
          }
        }

        .page-size-select {
          padding: var(--space-small);
          border: 1px solid var(--s-300);
          border-radius: var(--border-radius-small);
          font-size: var(--font-size-small);
          background: white;

          &:focus {
            outline: none;
            border-color: var(--n-brand);
          }
        }
      }
    }

    .content-loading {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-normal);
      color: var(--s-600);
    }
  }

  .contacts-grid {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .contacts-table-wrapper {
      flex: 1;
      overflow: auto;
    }
  }

  .contacts-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: var(--space-small);
      text-align: left;
      border-bottom: 1px solid var(--s-200);
    }

    th {
      background-color: var(--s-50);
      font-weight: var(--font-weight-medium);
      font-size: var(--font-size-small);
      color: var(--s-700);
      position: sticky;
      top: 0;
      z-index: 1;
    }

    tr {
      &:hover {
        background-color: var(--s-25);
      }

      &.selected {
        background-color: var(--n-brand-light, var(--s-75));
      }
    }

    td {
      font-size: var(--font-size-small);
    }

    .contact-name {
      font-weight: var(--font-weight-medium);
      color: var(--s-900);
    }

    .contact-email,
    .contact-phone {
      color: var(--s-700);
    }

    .contact-date {
      color: var(--s-600);
      font-size: var(--font-size-mini);
    }
  }

  .contact-labels {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;

    .no-labels {
      color: var(--s-500);
      font-style: italic;
      font-size: var(--font-size-mini);
    }
  }

  .label-badge {
    display: inline-block;
    padding: var(--space-micro) var(--space-small);
    border-radius: var(--border-radius-small);
    color: white;
    font-size: var(--font-size-mini);
    font-weight: var(--font-weight-medium);

    &.mini {
      padding: 2px 6px;
      font-size: 11px;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .pagination {
    padding: var(--space-normal);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--space-small);
    border-top: 1px solid var(--s-200);

    .pagination-btn {
      padding: var(--space-small) var(--space-normal);
      border: 1px solid var(--s-300);
      border-radius: var(--border-radius-small);
      background: white;
      color: var(--s-700);
      font-size: var(--font-size-small);
      cursor: pointer;
      transition: all 0.2s;

      &:hover:not(:disabled) {
        background: var(--s-50);
        border-color: var(--n-brand);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .pagination-info {
      font-size: var(--font-size-small);
      color: var(--s-700);
      margin: 0 var(--space-small);
    }
  }

  .selected-actions {
    padding: var(--space-normal);
    background: var(--n-brand-light, var(--s-50));
    border-top: 1px solid var(--s-200);
    display: flex;
    justify-content: space-between;
    align-items: center;

    .selected-info {
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-medium);
      color: var(--n-brand);
    }
  }

  .button {
    display: inline-flex;
    align-items: center;
    gap: var(--space-small);
    padding: var(--space-small) var(--space-normal);
    border: 1px solid transparent;
    border-radius: var(--border-radius-small);
    background: transparent;
    color: var(--s-700);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: var(--s-50);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--primary {
      background: var(--n-brand);
      color: white;
      border-color: var(--n-brand);

      &:hover:not(:disabled) {
        background: var(--n-brand-dark, var(--n-brand));
        transform: translateY(-1px);
      }
    }

    &--secondary {
      background: var(--s-100);
      color: var(--s-800);
      border-color: var(--s-300);

      &:hover:not(:disabled) {
        background: var(--s-200);
      }
    }
  }

  .no-data {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: var(--space-large);
    color: var(--s-600);

    p {
      margin: 0;
    }
  }
}
</style>
